import { useEffect, useState } from "react";

import { UUID } from "crypto";
import { useSelector } from "react-redux";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from "@mui/material";

import { DialogProps, useDialogs } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store";
import { DialogoBaseProps } from "@interfaces/components";

import { useLazyGetStatusConfigQuery } from "@services/api/catalogs";

import CheckIcon from "@mui/icons-material/Check";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import { Notificacion } from "@components/DialogosSegmentos/Notificacion";
import { useChangeStatusApp } from "@hooks/useChangeStatusApp";
import { ApplicationMarketplaceResume, ApplicationResume } from "@interfaces/application";

interface EdicionEstatusDialogProps extends DialogoBaseProps {
    productId?: UUID;
    taxSystemId?: UUID;
    updateStore?: boolean;
}

export const EdicionEstatusDialog = (
    props: DialogProps<EdicionEstatusDialogProps, ApplicationResume | ApplicationMarketplaceResume | void>,
) => {
    const dialogs = useDialogs();

    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);

    const [statusSelected, setStatusSelected] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [saveState, setSaveState] = useState<boolean | null>(false);

    const { updateStatusApp } = useChangeStatusApp();

    const [triggerGetStatusConfig, getStatusConfigResult] = useLazyGetStatusConfigQuery();

    const handleOnChangeClicked = async () => {
        try {
            setSaving(true);

            const result = await updateStatusApp(statusSelected, true, props.payload.updateStore ?? true);

            setTimeout(() => {
                setSaveState(true);
                props.onClose(result);
                setSaving(false);
            }, 600);
        } catch (error) {
            console.log(error);
            setSaveState(false);

            await dialogs.open(Notificacion, {
                type: "WARNING",
                text: "No se pudo cambiar el estatus de la solicitud. Error interno.",
            });

            setSaving(false);
        } finally {
        }
    };

    const loadInfo = async () => {
        await triggerGetStatusConfig({
            productId: props.payload.productId ?? applicationSelected.product_id,
            taxSystemId: props.payload.taxSystemId ?? applicationSelected.tax_system.id,
        }).unwrap();

        setStatusSelected(applicationSelected.status_code);
    };

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0") return;

        loadInfo();
    }, [applicationSelected]);

    return (
        <Dialog fullWidth={props.payload.fullWidth ?? false} open={props.open}>
            <DialogContent>
                <Box>
                    <Stack
                        sx={{
                            rowGap: 2,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="body1"
                            component="span"
                            sx={{
                                color: "main3",
                                fontWeight: 600,
                            }}
                        >
                            Está seguro que quiere cambiar el estatus de:
                        </Typography>
                        <Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    color: "#528CD6",
                                }}
                            >
                                {applicationSelected.company_name}
                            </Typography>
                        </Box>
                        <Typography
                            variant="h4"
                            sx={{
                                color: "#528CD6",
                            }}
                        >
                            ID SOLICITUD:{" "}
                            <Typography
                                component={"span"}
                                variant="h4"
                                sx={{
                                    fontWeight: 600,
                                }}
                            >
                                {applicationSelected.id_application_company}
                            </Typography>{" "}
                        </Typography>
                        <FormControl fullWidth margin={"normal"} sx={{ textAlign: "left" }}>
                            <InputLabel required>Selecciona un estatus</InputLabel>
                            <Select
                                label="Selecciona un estatus"
                                value={statusSelected}
                                onChange={(value: SelectChangeEvent) => setStatusSelected(value.target.value)}
                                MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                            >
                                {[...(getStatusConfigResult.currentData ?? [])]
                                    ?.sort((a, b) => a.sequence - b.sequence)
                                    .map((option, idx) => (
                                        <MenuItem key={idx} value={option.status_catalog.code}>
                                            {option.sequence} - {option.status_catalog.name} (
                                            {option.status_catalog.code})
                                            {option.notification_template !== null && (
                                                <MarkunreadIcon sx={{ ml: 1 }} color="main4" />
                                            )}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions sx={{ mb: 2, justifyContent: "space-evenly" }}>
                <Button variant="blue_outlined" disabled={saving} onClick={() => props.onClose()}>
                    Regresar
                </Button>
                <Button
                    variant="contained"
                    color={saveState === false ? "primary" : "success"}
                    startIcon={saveState === false ? <></> : <CheckIcon />}
                    disabled={statusSelected === applicationSelected.status_code || saving}
                    onClick={handleOnChangeClicked}
                    loading={saving}
                >
                    {saveState ? "Actualizado" : "Cambiar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
